import type {
  CareSignalView,
  HealthStoryEntry,
  HealthSummaryMedicationItem,
  TimelineEvent,
  TimelineViewResponse,
} from '@troyai/portal/health-timeline/data-access';
import {
  demoPersona,
  scriptedEventUuid,
  storyDate,
  storyIso,
} from '@troyai/portal/investor-demo/core';

import type { Visible } from '../../demo-data';
import { CLINICAL_EVENT_ID_BASE } from '../ids';
import {
  type BuiltStory,
  buildCareSignals,
  buildStory,
  CONDITIONS,
  DICLOFENAC_VISIBLE_FROM,
  medicationItems,
  type StorySeed,
} from '../timeline.fixture';

/**
 * The pharmacy story's Health story and Timeline cards: the care story's claims, visits and
 * coverage, with Park Road Pharmacy's calls and notes in place of the care manager's, and the
 * statin pickup after the "+3 days" jump. It builds its own entries, so its clinical event ids
 * differ from the care story's `KEY_EVENTS`, which nothing here reads. Built inside the calls,
 * never as module constants, so the perspective is read when the store seeds.
 */

/** The pharmacy's calls and notes and the pickup fill, on top of the shared claims story. */
function pharmacyStory(): BuiltStory {
  // "You" in prose: the name without credentials, read per call (the account arrives at runtime).
  const you = demoPersona().name.split(',')[0];
  // Held back until the "+3 days" jump, like the diclofenac fill.
  const afterJump = { visibleFrom: DICLOFENAC_VISIBLE_FROM };
  const interaction = (
    date: string,
    title: string,
    summary: string,
    tags: string[],
    statusLabel: string,
    extra: Partial<StorySeed> = {}
  ): StorySeed => ({
    category: 'interaction',
    date: storyIso(date),
    title,
    summary,
    tags,
    statusLabel,
    ...extra,
  });

  const interactions = [
    interaction(
      '2026-09-26',
      'Refill reminder · Troy Agent · SMS',
      'Atorvastatin is due and has no refills left; asked Dr. Cole for a renewal.',
      ['Refill reminder'],
      'Delivered'
    ),
    interaction(
      '2026-09-28',
      'Renewal request · Troy Agent · e-Rx',
      'Atorvastatin 20 mg renewal sent to Dr. Amanda Cole',
      ['Prescriber request'],
      'Sent'
    ),
    interaction(
      '2026-10-02',
      "Call to Dr. Cole's office · Troy Agent · 4 min",
      'Renewal not reviewed yet; the nurse will route it today.',
      ['Prescriber request'],
      'Completed'
    ),
    interaction(
      '2026-10-05',
      'Inbound call · Troy Agent · 7 min',
      'Asked why her Jardiance copay went up to $47. Explained her coverage phase; offered to look for help.',
      ['Insurance question'],
      'Answered'
    ),
    interaction(
      '2026-10-06',
      'Call to plan member services · Troy Agent · 22 min',
      '17 min on hold. Confirmed the tier change; she can apply for Extra Help.',
      ['Insurance question'],
      'Completed'
    ),
    interaction(
      '2026-10-07',
      'Education call · Troy Agent · 9 min',
      'Why NSAIDs strain the heart and kidneys; daily weights; confirmed a scale at home.',
      ['Medication education', 'Heart failure'],
      'Completed'
    ),
    interaction(
      '2026-10-07',
      'Fax to Dr. Patel · Troy Agent',
      'Asked to stop meloxicam; suggested topical diclofenac.',
      ['Prescriber request'],
      'Sent'
    ),
    interaction(
      '2026-10-08',
      'Diabetes lesson · Troy Agent · SMS',
      'Sent "Sick-day rules for Jardiance and metformin"; she opened it.',
      ['Disease education', 'Diabetes'],
      'Read'
    ),
    interaction(
      '2026-10-09',
      'Call to manufacturer assistance · Troy Agent · 52 min',
      '38 min on hold. She can apply; application packet received by fax.',
      ['Patient assistance'],
      'Completed'
    ),
    interaction(
      '2026-10-11',
      'Inbound call · Troy Agent · 6 min',
      'After hours, pharmacy closed. Asked whether she can "just stop the pain pill": flagged for the pharmacist.',
      ['Medication question'],
      'Escalated'
    ),
    interaction(
      '2026-10-12',
      'Prescriber reply · Dr. Cole · e-Rx',
      'Atorvastatin 20 mg approved: 90-day supply, 3 refills.',
      ['Prescriber request'],
      'Approved'
    ),
    interaction(
      '2026-10-12',
      'Pickup call · Troy Agent · 3 min',
      `Atorvastatin ready with 3 synced refills. Booked ${storyDate('2026-10-15', 'EEEE')} 10:30 and a ride through her plan.`,
      ['Pickup reminder'],
      'Completed'
    ),
    interaction(
      '2026-10-14',
      `Call to Dr. Patel's office · ${you} · 5 min`,
      'Agreed to stop meloxicam; diclofenac gel e-prescribed.',
      ['Prescriber request'],
      'Completed',
      afterJump
    ),
    interaction(
      '2026-10-15',
      'Pickup reminder · Troy Agent · SMS',
      'Your ride arrives at 10:10.',
      ['Pickup reminder'],
      'Delivered',
      afterJump
    ),
    interaction(
      '2026-10-15',
      `Counseling at pickup · ${you}`,
      'Stops meloxicam today; showed how to use diclofenac gel; reviewed daily weights.',
      ['Medication education'],
      'Completed',
      afterJump
    ),
    interaction(
      '2026-10-15',
      `Vaccines given · ${you}`,
      'Fluzone High-Dose and Prevnar 20 at pickup; reported to the state registry.',
      ['Immunization'],
      'Given',
      { ...afterJump, key: 'vaccinesGiven' }
    ),
  ];

  const atorvastatinPickup: StorySeed = {
    key: 'atorvastatinPickup',
    category: 'medication',
    date: storyIso('2026-10-15'),
    title: 'Filled Atorvastatin Calcium 20 mg Tablet',
    summary: '90-day supply · Qty 90',
    tags: [CONDITIONS.lipids, CONDITIONS.diabetes],
    statusLabel: 'Filled',
    recentlyUpdated: true,
    ...afterJump,
  };

  return buildStory(interactions, [atorvastatinPickup]);
}

/** The pharmacy story's Health story, newest first (97 entries). */
export function seedPharmacyStoryEntries(): Visible<HealthStoryEntry>[] {
  return pharmacyStory().entries;
}

/**
 * The pharmacy story's Timeline cards, pointing at its own entries. The pneumococcal vaccine has
 * no claim behind it, so its gap points at no story entry.
 */
export function seedPharmacyCareSignals(): Record<CareSignalView, TimelineViewResponse> {
  const keyed = pharmacyStory().keyed;

  const pneumococcalGap: TimelineEvent = {
    event_id: scriptedEventUuid(CLINICAL_EVENT_ID_BASE + 99),
    version: 1,
    lane: 'medical_claim',
    event_type: 'care_gap',
    status: 'active',
    valid_from: storyIso('2026-01-01'),
    valid_to: null,
    details: {
      detail: 'No PCV on record · 65 and over',
      note: 'Screened from claims and the state registry',
    },
    view_context: {
      kind: 'care_gap',
      state: 'open',
      measure: 'Pneumococcal vaccine',
      gap_start: storyIso('2026-01-01'),
      gap_end: '',
      duration_days: 285,
    },
  };

  // The upcoming cards open the entry that fills them once the jump shows it (the pickup fill and
  // the vaccines given), and read as their own event before.
  const medSyncPickup: TimelineEvent = {
    event_id: keyed.atorvastatinPickup.event_id,
    version: 1,
    lane: 'pharmacy_fill',
    event_type: 'care_due',
    status: 'active',
    valid_from: storyIso('2026-10-15'),
    valid_to: null,
    details: {
      detail: `Due ${storyDate('2026-10-15', 'MMM d')} · in 2 days`,
      note: '4 meds synced · ride booked',
    },
    view_context: {
      kind: 'care_due',
      measure: 'Med sync pickup',
      due_date: storyIso('2026-10-15'),
      days_until_due: 2,
    },
  };

  const fluVaccine: TimelineEvent = {
    event_id: keyed.vaccinesGiven.event_id,
    version: 1,
    lane: 'medical_claim',
    event_type: 'care_due',
    status: 'active',
    valid_from: storyIso('2026-10-31'),
    valid_to: null,
    details: {
      detail: `Due ${storyDate('2026-10-31', 'MMM d')} · in 18 days`,
      note: 'Part of the fall immunizations episode',
    },
    view_context: {
      kind: 'care_due',
      measure: 'Flu vaccine',
      due_date: storyIso('2026-10-31'),
      days_until_due: 18,
    },
  };

  return buildCareSignals(keyed, {
    gaps: [pneumococcalGap],
    upcoming: [medSyncPickup, fluVaccine],
  });
}

/** The Health summary's medication list: each drug's latest visible fill in the pharmacy story. */
export function seedPharmacyMedicationItems(): HealthSummaryMedicationItem[] {
  return medicationItems(pharmacyStory());
}
